// Google Tag Manager & Analytics Utility Functions
// This file contains helper functions for tracking events

declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
    fbq: any;
    _fbq: any;
  }
}

// Initialize dataLayer if it doesn't exist
window.dataLayer = window.dataLayer || [];

/**
 * Push event to Google Tag Manager dataLayer
 */
export const pushToDataLayer = (event: string, data: Record<string, any> = {}) => {
  window.dataLayer.push({
    event,
    ...data
  });
};

/**
 * Track WhatsApp click conversion
 */
export const trackWhatsAppClick = (phoneNumber: string) => {
  pushToDataLayer('whatsapp_click', {
    event_category: 'engagement',
    event_label: 'WhatsApp Button Click',
    phone_number: phoneNumber,
    conversion_type: 'whatsapp_contact'
  });
  
  // Also track as conversion for Facebook Pixel
  if (typeof window.fbq !== 'undefined') {
    window.fbq('track', 'Contact', {
      content_name: 'WhatsApp Click',
      content_category: 'Contact'
    });
  }
};

/**
 * Track phone call click conversion
 */
export const trackPhoneClick = (phoneNumber: string) => {
  pushToDataLayer('phone_click', {
    event_category: 'engagement',
    event_label: 'Phone Call Click',
    phone_number: phoneNumber,
    conversion_type: 'phone_contact'
  });
  
  // Also track as conversion for Facebook Pixel
  if (typeof window.fbq !== 'undefined') {
    window.fbq('track', 'Contact', {
      content_name: 'Phone Call Click',
      content_category: 'Contact'
    });
  }
};

/**
 * Track contact form submission
 */
export const trackContactFormSubmit = (formData: {
  name?: string;
  email?: string;
  phone?: string;
  message?: string;
}) => {
  pushToDataLayer('contact_form_submit', {
    event_category: 'engagement',
    event_label: 'Contact Form Submission',
    conversion_type: 'form_submission',
    form_name: 'Contact Form'
  });
  
  // Track as lead for Facebook Pixel
  if (typeof window.fbq !== 'undefined') {
    window.fbq('track', 'Lead', {
      content_name: 'Contact Form',
      content_category: 'Lead Generation'
    });
  }
};

/**
 * Track Instagram click
 */
export const trackInstagramClick = (postId: string, postCaption: string) => {
  pushToDataLayer('instagram_click', {
    event_category: 'engagement',
    event_label: 'Instagram Post Click',
    post_id: postId,
    post_caption: postCaption
  });
};

/**
 * Track product view
 */
export const trackProductView = (productName: string, category: string) => {
  pushToDataLayer('product_view', {
    event_category: 'ecommerce',
    event_label: 'Product View',
    product_name: productName,
    product_category: category
  });
  
  // Track as ViewContent for Facebook Pixel
  if (typeof window.fbq !== 'undefined') {
    window.fbq('track', 'ViewContent', {
      content_name: productName,
      content_category: category,
      content_type: 'product'
    });
  }
};

/**
 * Track category view
 */
export const trackCategoryView = (categoryName: string) => {
  pushToDataLayer('category_view', {
    event_category: 'navigation',
    event_label: 'Category View',
    category_name: categoryName
  });
};

/**
 * Track page view (for SPA navigation)
 */
export const trackPageView = (pagePath: string, pageTitle: string) => {
  pushToDataLayer('page_view', {
    page_path: pagePath,
    page_title: pageTitle
  });
};

/**
 * Track social media click
 */
export const trackSocialClick = (platform: string, location: string) => {
  pushToDataLayer('social_click', {
    event_category: 'engagement',
    event_label: `${platform} Click`,
    social_platform: platform,
    click_location: location
  });
};

/**
 * Track scroll depth
 */
export const trackScrollDepth = (percentage: number) => {
  pushToDataLayer('scroll_depth', {
    event_category: 'engagement',
    event_label: `Scroll Depth ${percentage}%`,
    scroll_percentage: percentage
  });
};

/**
 * Initialize Facebook Pixel (call this after GTM loads)
 * Note: This is typically handled by GTM. Use only if not using GTM.
 */
export const initFacebookPixel = (pixelId: string) => {
  if (typeof window.fbq === 'undefined') {
    (function(f: any, b: any, e: any, v: any, n?: any, t?: any, s?: any) {
      if (f.fbq) return;
      n = f.fbq = function(...args: any[]) {
        n.callMethod ? n.callMethod.apply(n, args) : n.queue.push(args);
      };
      if (!f._fbq) f._fbq = n;
      n.push = n;
      n.loaded = !0;
      n.version = '2.0';
      n.queue = [];
      t = b.createElement(e);
      t.async = !0;
      t.src = v;
      s = b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t, s);
    })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
  }
  
  // Initialize after script loads
  if (window.fbq) {
    window.fbq('init', pixelId);
    window.fbq('track', 'PageView');
  }
};
